import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
    Button,
    Menu,
    MenuItem,
    Switch,
    FormControlLabel,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/pro-light-svg-icons';
import DebouncedTextField from '@commonClient2/components/DebouncedTextField';
import { displayNotificationCustomTags } from '../../../store/appData';
import './ContentSection.scss';

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

const InputDisclaimer = (props) => (
    <p {...props}>
        <small className="input-disclaimer">
            {props.children}
        </small>
    </p>
);

const addDetailsToString = (/** @type {any} */ string) => `${string}Details`;

/**
 * @param {{
 *  stepIndex: number
 *  validationErrors: {[Key: string] : boolean | string}
 *  errorRef: import('react').MutableRefObject<HTMLInputElement>
 *  formData: any
 *  updateFormData: Function
 * }} props
 */
function ContentSection({ stepIndex, validationErrors, errorRef, formData, updateFormData }) {
    const { t } = useTranslation('notificationTemplates');
    const { t: tTags } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.notificationStep.tags' });
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const textAreaRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);

    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminAppData } = useSelector((state) => state);
    const { notificationCustomTags } = adminAppData;

    // Load notification tags on mount
    useEffect(() => {
        if (!notificationCustomTags || notificationCustomTags.length === 0) {
            dispatch(displayNotificationCustomTags());
        }
    }, [dispatch, notificationCustomTags]);

    // Map tags to the format expected by the menu
    const NOTIFICATION_TAGS = (notificationCustomTags || []).map((tag) => ({
        mapName: tag,
        description: tTags(tag),
        descriptionDetails: tTags(addDetailsToString(tag)),
    }));

    const handleInsertTag = (tag) => {
        setAnchorEl(null);

        // Simply append the tag to the end of the content
        const currentContent = formData.templateContent || '';
        const newContent = `${currentContent} {{ ${tag.mapName} }} `;
        updateFormData('templateContent', newContent);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const content = e.target?.result;
                    // In a real implementation, you would sanitize the HTML content here
                    updateFormData('templateContent', content);
                    updateFormData('isFile', true);
                };
                reader.readAsText(file);
            } catch (error) {
                console.error('Error reading file:', error);
            }
        }
    };

    const handleRemoveFile = () => {
        updateFormData('templateContent', '');
        updateFormData('isFile', false);
    };

    return (
        <Card className="card">
            <CardHeader
                ref={errorRef}
                className="card--header"
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('sidePanel.contentSection.title')}
            />
            <CardContent>
                <div>
                    <InputTitle>{t('sidePanel.contentSection.templateSubjectLabel')}</InputTitle>
                    <DebouncedTextField
                        error={validationErrors.templateSubject}
                        value={formData.templateSubject || ''}
                        onChange={(event) => updateFormData('templateSubject', event.target.value)}
                        inputProps={{ maxLength: 50 }}
                        placeholder={t('sidePanel.contentSection.templateSubjectPlaceholder')}
                        variant="outlined"
                        fullWidth
                    />
                    <InputDisclaimer>{t('sidePanel.contentSection.templateSubjectDisclaimer')}</InputDisclaimer>
                </div>

                <div className="content-actions">
                    {!formData.isFile && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            className="insert-tag-button"
                        >
                            {t('sidePanel.contentSection.insertTag')}
                        </Button>
                    )}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                    >
                        {NOTIFICATION_TAGS.map((tag) => (
                            <MenuItem
                                key={tag.mapName}
                                onClick={() => handleInsertTag(tag)}
                            >
                                {tag.description}
                            </MenuItem>
                        ))}
                    </Menu>

                    {/* File upload feature hidden for now */}
                    {/* {formData.isFile ? (
                        <Button
                            variant="outlined"
                            onClick={handleRemoveFile}
                            className="remove-file-button"
                        >
                            {t('sidePanel.contentSection.removeFile')}
                        </Button>
                    ) : (
                        <div className="file-upload-wrapper">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".html,.htm"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                                id="file-upload-input"
                            />
                            <label htmlFor="file-upload-input" className="file-upload-label">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<FontAwesomeIcon icon={faUpload} />}
                                >
                                    {t('sidePanel.contentSection.uploadFile')}
                                </Button>
                            </label>
                        </div>
                    )} */}
                </div>

                <div ref={textAreaRef}>
                    <InputTitle>{t('sidePanel.contentSection.templateContentLabel')}</InputTitle>
                    {formData.isFile ? (
                        <div
                            className="file-content-preview"
                            dangerouslySetInnerHTML={{ __html: formData.templateContent || '' }}
                        />
                    ) : (
                        <DebouncedTextField
                            error={validationErrors.templateContent}
                            value={formData.templateContent || ''}
                            onChange={(event) => updateFormData('templateContent', event.target.value)}
                            multiline
                            rows={10}
                            placeholder={t('sidePanel.contentSection.templateContentPlaceholder')}
                            variant="outlined"
                            fullWidth
                        />
                    )}
                    <InputDisclaimer>{t('sidePanel.contentSection.templateContentDisclaimer')}</InputDisclaimer>
                </div>

                <div className="availability-toggle">
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={formData.isAvailableForUse || false}
                                onChange={(event) => updateFormData('isAvailableForUse', event.target.checked)}
                                color="primary"
                            />
                        )}
                        label={
                            formData.isAvailableForUse
                                ? t('sidePanel.contentSection.isAvailableForUse.true')
                                : t('sidePanel.contentSection.isAvailableForUse.false')
                        }
                    />
                </div>
            </CardContent>
        </Card>
    );
}

export default ContentSection;

